import React, { useCallback, useEffect, useState } from 'react';

import { Container, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ExamSummaryFilters from './components/ExamSummaryFilters';
import ExamSummaryTable from './components/ExamSummaryTable';
import type { ExamSummaryDTO } from './models/examSummaryDTOs';
import type { FilterState } from './models/filterDTOs';
import { useAuth } from '@/src/app/context/useAuth';

const ExamSummaryPage: React.FC = () => {
    document.title = 'Exam Summary';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Array<ExamSummaryDTO>>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    viewMode: parsed.viewMode || 1,
                    examTypeMasterId: parsed.examTypeMasterId || 0,
                    examIds: parsed.examIds || [],
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            viewMode: 1,
            examTypeMasterId: 0,
            examIds: [],
        };
    });

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            const existingFilters = saved ? JSON.parse(saved) : {};

            const universalFilters = {
                ...existingFilters,
                gradeLevelMasterId: filterState.gradeLevelMasterId,
                districtId: filterState.districtId,
                schoolId: filterState.schoolId,
                classId: filterState.classId,
                examTypeMasterId: filterState.examTypeMasterId,
                viewMode: filterState.viewMode,
                examIds: filterState.examIds,
            };
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const refreshTableData = useCallback(
        async ({
            gradeLevelMasterId,
            districtId,
            schoolId,
            classId,
            examTypeMasterId,
            examIds,
            viewMode,
        }: FilterState = filterState) => {
            setError('');
            setLoaded(false);
            setHasSearched(true);

            // **Once there is more data in the DB, remove "/test" to use regular endpoint
            let requestUrl = `reports/examSummary?`;
            requestUrl += `districtId=${districtId}`;
            requestUrl += `&schoolId=${schoolId}`;
            requestUrl += `&classId=${classId}`;
            requestUrl += `&gradeLevelMasterId=${gradeLevelMasterId}`;
            requestUrl += `&examTypeMasterId=${examTypeMasterId}`;
            const queryParams = examIds.map((id) => `examIds=${id}`).join('&');
            requestUrl += `&${queryParams}`;
            requestUrl += `&viewMode=${viewMode}`;

            try {
                const res = await apiRequest<ExamSummaryResponse>('GET', requestUrl);
                setData(res.data.data);
            } catch {
                setError('Failed to load Exam Summary Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    type ExamSummaryResponse = {
        viewMode: string;
        data: ExamSummaryDTO[];
    };

    return (
        <Container>
            <h1 className="page-header">Exam Summary Report</h1>
            <br />

            <ExamSummaryFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            <ErrorMessage error={hasSearched ? error : ''} />

            {hasSearched && <ExamSummaryTable data={data} isLoading={!loaded} error={error} />}
        </Container>
    );
};

export default ExamSummaryPage;
